import { Controller } from "@hotwired/stimulus"

// Enhances the branch picker inside the location modal:
//   - On connect, asks the browser for geolocation. While resolving,
//     a "Finding your location…" banner is shown.
//   - On success, computes the great-circle distance from the user to
//     each branch, splits them into "Near you" (within `nearKm`) and
//     "All branches", reorders both groups by distance, and reveals the
//     "Near you" section header.
//   - On denial / timeout / error, the banner flips to a neutral notice
//     ("Using the full branch list") and the server-rendered flat list
//     stays visible.
//   - Whenever the user picks a radio, that branch's label is moved to
//     the top of its own group so the selection stays sticky when the
//     modal re-opens or the list re-sorts.
export default class extends Controller {
  static targets = ["branch", "nearYou", "nearYouHeading", "status", "statusMessage"]
  static values = {
    nearKm: { type: Number, default: 10 },
    timeoutMs: { type: Number, default: 8000 },
  }

  connect() {
    this.#pinSelected() // sticky selected-first on re-open

    if (!("geolocation" in navigator)) {
      this.#showStatus("Using the full branch list.", true)
      return
    }

    this.#showStatus("Finding your location…")
    navigator.geolocation.getCurrentPosition(
      (pos) => this.#onLocated(pos.coords.latitude, pos.coords.longitude),
      () => this.#showStatus("Using the full branch list.", true),
      { enableHighAccuracy: false, maximumAge: 300_000, timeout: this.timeoutMsValue }
    )
  }

  // Called when the user ticks a radio — keeps the picked branch pinned.
  select(event) {
    this.#pinSelected()
  }

  // --- private -----------------------------------------------------------

  #onLocated(lat, lng) {
    const near = []
    const far = []

    for (const label of this.branchTargets) {
      const blat = parseFloat(label.dataset.branchLat)
      const blng = parseFloat(label.dataset.branchLng)
      if (Number.isNaN(blat) || Number.isNaN(blng)) {
        far.push({ el: label, distance: Infinity })
        continue
      }
      const d = this.#haversine(lat, lng, blat, blng)
      label.dataset.branchDistance = d.toFixed(2)
      const distEl = label.querySelector("[data-branch-picker-target='distance']")
      if (distEl) distEl.textContent = `${d.toFixed(1)} km away`
      ;(d <= this.nearKmValue ? near : far).push({ el: label, distance: d })
    }

    near.sort((a, b) => a.distance - b.distance)
    far.sort((a, b) => a.distance - b.distance)

    if (near.length > 0 && this.hasNearYouTarget) {
      for (const { el } of near) this.nearYouTarget.appendChild(el)
      this.nearYouTarget.classList.remove("hidden")
      if (this.hasNearYouHeadingTarget) {
        this.nearYouHeadingTarget.classList.remove("hidden")
      }
    }
    // Whatever wasn't moved up is already in its original container.
    // Re-append in sorted order so distance ordering holds.
    const allContainer = this.element.querySelector(
      "[data-branch-picker-target='all']"
    )
    if (allContainer) {
      for (const { el } of far) allContainer.appendChild(el)
    }

    this.#hideStatus()
    this.#pinSelected()
  }

  #pinSelected() {
    const checked = this.branchTargets.find((el) => el.querySelector("input[type=radio]")?.checked)
    if (!checked) return
    const parent = checked.parentElement
    if (parent && parent.firstElementChild !== checked) {
      parent.insertBefore(checked, parent.firstElementChild)
    }
  }

  #showStatus(message, muted = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.classList.remove("hidden")
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
    }
    this.statusTarget.classList.toggle("text-slate-500", muted)
    this.statusTarget.classList.toggle("text-slate-700", !muted)
    if (muted) {
      setTimeout(() => this.#hideStatus(), 2500)
    }
  }

  #hideStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.classList.add("hidden")
  }

  // Great-circle distance in kilometres (Haversine formula).
  #haversine(lat1, lng1, lat2, lng2) {
    const toRad = (deg) => (deg * Math.PI) / 180
    const R = 6371 // km
    const dLat = toRad(lat2 - lat1)
    const dLng = toRad(lng2 - lng1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(a))
  }
}
